import SkipSentryError from 'Errors/SkipSentryError';
import * as Sentry from '@sentry/react';
import { LinkedErrors } from '@sentry/browser';
import { BrowserTracing } from '@sentry/tracing';
import history from 'State/history';

export const INTENTIONALLY_ABORTED_SIGNAL = 'intentionally aborted signal';

// All of these errors will not be logged to sentry, ever
const SENTRY_ERROR_IGNORE_LIST = [
  'Request has been terminated Possible causes: the network is offline, Origin is not allowed by Access-Control-Allow-Origin, the page is being unloaded, etc.',
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications.',
  'Unauthorized',
  '[{"title":"Unauthorized"}]',
  '{"title":"Unauthorized"}', // Support the old style of this error, where we don't return an array
  '[{"title":"Forbidden","detail":"Forbidden"}]',
  '{"title":"Forbidden","detail":"Forbidden"}', // Support the old style of this error, where we don't return an array
  "Whoops! Let's try again.",
  '[{"title":"Whoops! Let\'s try again."}]', // There is no old style of this error to support :) it's newer
  'Failed to retrieve Business Hours data', // expected salesforce chat widget error when no chat hours are available
  'Unable to load Embedded Messaging configuration', // expected salesforce chat widget error when no chat hours are available
  INTENTIONALLY_ABORTED_SIGNAL,
];

function checkSentryDisabled() {
  switch (process.env.REACT_APP_STAGE) {
    // case 'development': // <-- enable for local sentry testing
    case 'development-minified':
    case 'prod':
    case 'staging':
      return false;
    default:
      return true;
  }
}

const sentryDisabled = checkSentryDisabled();

const safelyAddTraceIdToBreadcrumb = (breadcrumb, hint) => {
  try {
    if (hint.xhr.getAllResponseHeaders().indexOf('x-trace-id') >= 0) {
      const traceId = hint.xhr.getResponseHeader('x-trace-id');
      if (traceId) {
        return { ...breadcrumb, data: { ...breadcrumb.data, traceId } };
      }
    }
  } catch {
    return breadcrumb;
  }

  return breadcrumb;
};


function init() {
  if (sentryDisabled) return;
  Sentry.init({
    ignoreErrors: SENTRY_ERROR_IGNORE_LIST,
    environment: process.env.REACT_APP_STAGE,
    release: process.env.REACT_APP_SENTRY_RELEASE,
    dsn: 'https://3ebf8a422947490ba52751600075eda0@o346382.ingest.sentry.io/2048398',
    tracesSampleRate: 0.25,
    integrations: [
      new BrowserTracing({
        routingInstrumentation: Sentry.reactRouterV5Instrumentation(history),
        // We log a LOT to Sentry. If we log all of our API requests to Sentry, that would probably be too much
        traceFetch: false,
        traceXHR: false,
      }),
      new LinkedErrors({ limit: 20 }),
    ],
    beforeBreadcrumb(breadcrumb, hint) {
      switch (breadcrumb.category) {
        case 'console': {
          // We abuse the console, which makes our POSTs to sentry fail with a 413 response code
          return null;
        }
        case 'xhr': {
          return safelyAddTraceIdToBreadcrumb(breadcrumb, hint);
        }
        default: {
          return breadcrumb;
        }
      }
    },
  });
}

function setUser({ id, username }) {
  if (sentryDisabled) return;

  Sentry.configureScope((scope) => {
    scope.setUser({
      userId: id,
      email: username,
    });
  });
}

export function setVisitorSessionForNonLoggedInUser(visitorSessionUUID) {
  if (sentryDisabled) return;

  Sentry.configureScope((scope) => {
    scope.setUser({ visitorSessionUUID });
  });
}

const createErrorMessage = (errs) => {
  let errorMessage = '';
  const commaString = ', ';
  errs.forEach((err, i) => {
    if (i + 1 < errs.length) {
      errorMessage += err.shortDetail
        ? err.shortDetail + commaString
        : err.detail + commaString;
    } else {
      errorMessage += err.shortDetail ? err.shortDetail : err.detail;
    }
  });
  return errorMessage;
};

const getTraceIdOrDefault = (response, defaultTraceId = 'Unknown') => {
  return response?.headers['x-trace-id'] || defaultTraceId;
};

const cleanUpErrorMessage = (message) => {
  if (!message || typeof message !== 'string') {
    return message;
  }
  return message.replace(/\n/g, ' '); // <-- Removes any potential line breaks in error message
};

const buildProperSentryError = (err) => {
  const sentryError = err;
  if (Array.isArray(err.errors)) {
    err.errors.forEach(el => {
      const { title, detail } = el;
      if (title) {
        sentryError.name += ` | ${title}`;
        if (detail) {
          sentryError.name += `: ${detail}`;
        }
      }
    });
  }
  return sentryError;
};

const buildSentryFingerprint = (err) => {
  const fingerprint = [];
  if (Array.isArray(err.errors)) {
    err.errors.forEach(el => {
      let name = '';
      const { title } = el;
      if (title) {
        name += title;
        fingerprint.push(name);
      }
    });
  }
  return fingerprint;
};

const createSentryErrorLog = (err, statusCode, { componentStack } = {}) => {
  tracker.handleError(err); if (statusCode !== undefined && parseInt(statusCode, 10) < 500) {
    return null;
  }

  const errorMessage = Array.isArray(err) ? createErrorMessage(err) : cleanUpErrorMessage(err?.message ?? err);

  // Don't even log this message if it's something we always ignore
  if (SENTRY_ERROR_IGNORE_LIST.includes(errorMessage)) {
    return null;
  }
  // Do not log to Sentry if error it is disabled in this env or if the error is an instance of SkipSentryError.
  if (sentryDisabled || err instanceof SkipSentryError) {
    console.error('SENTRY: ', err); // eslint-disable-line
    return null;
  }

  let sentryError = null;
  let fingerprint = null;

  // We already have a sentry error
  if (err instanceof Error && err.sentryScope) {
    let eventId;
    Sentry.withScope((scope) => {
      Object.keys(err.sentryScope).forEach((key) => {
        scope.setExtra(key, err.sentryScope[key]);
      });
      if (err.traceId) {
        scope.setTag('trace-id', err.traceId);
      } else if (err.response) {
        scope.setTag('trace-id', getTraceIdOrDefault(err.response));
      }

      eventId = Sentry.captureException(err);
    });

    return eventId;
  }

  // We need to build a sentry error
  if (err instanceof Error) {
    sentryError = buildProperSentryError(err);
    fingerprint = buildSentryFingerprint(err);
  } else if (Array.isArray(err)) {
    if (err.length === 1) {
      const { detail, title } = err[0];
      sentryError = new Error(JSON.stringify(detail));
      sentryError.name = JSON.stringify(title);
    } else {
      sentryError = new Error(createErrorMessage(err));
      sentryError.name = 'ArrayOfErrors';
    }
  } else if (err && err.detail && err.title) {
    sentryError = new Error(JSON.stringify(err.detail));
    sentryError.name = JSON.stringify(err.title);
  }

  let eventId;
  Sentry.withScope((scope) => {
    if (err.traceId) {
      scope.setTag('trace-id', err.traceId);
    } else if (err.response) {
      scope.setTag('trace-id', getTraceIdOrDefault(err.response));
    }

    if (fingerprint) {
      scope.setFingerprint(fingerprint);
    }

    let captureContext;
    if (componentStack) {
      captureContext = {
        contexts: {
          react: {
            componentStack,
          },
        },
      };
    }

    eventId = Sentry.captureException(sentryError || new Error(JSON.stringify(err)), captureContext);
  });

  return eventId;
};

export default {
  init,
  setUser,
  createSentryErrorLog,
  createErrorMessage,
};
